export interface JobAssignment {
  jobId: number;
  jobName: string;
  timeRange: string;
}

export interface ParsedStatusNotes {
  assignments: JobAssignment[];
  hasMultipleAssignments: boolean;
  filteredAssignments: JobAssignment[];
}

// More permissive time-range pattern: allows ASCII letters/digits, spaces,
// hyphen, en/em dashes, colon, dot, comma, slash, plus, and parentheses.
const TIME_RANGE_PATTERN = /^[\w\s\-–—:.,/+()]+$/u;

/**
 * Parses status notes to extract job assignment information
 * @param statusNotes - The status notes string to parse
 * @param currentJobId - Optional current job ID to filter out
 * @returns ParsedStatusNotes object with assignment details
 */
export const parseStatusNotes = (statusNotes: string | null | undefined, currentJobId?: number): ParsedStatusNotes => {
  if (!statusNotes) {
    return {
      assignments: [],
      hasMultipleAssignments: false,
      filteredAssignments: []
    };
  }

  // Check if the statusNotes contains job assignment information
  if (!statusNotes.includes('Employee') || !statusNotes.includes('is already assigned to job')) {
    return {
      assignments: [],
      hasMultipleAssignments: false,
      filteredAssignments: []
    };
  }

  // Split by "Employee" to separate each assignment
  const parts = statusNotes.split('Employee ').filter(Boolean);

  // If no parts found after splitting, return empty assignments
  if (parts.length === 0) {
    return {
      assignments: [],
      hasMultipleAssignments: false,
      filteredAssignments: []
    };
  }

  const assignments: JobAssignment[] = [];

  // Helper function to get the first successful job match
  const getJobMatch = (part: string): RegExpMatchArray | null => {
    // Try the enhanced regex with job name in parentheses first
    // Pattern: "is already assigned to job (\d+) \(([^)]*)\) from (.+?)"
    const enhancedMatch = part.match(/is already assigned to job (\d+) \(([^)]*)\) from (.+)/);
    if (enhancedMatch) {
      return enhancedMatch;
    }

    // Try the simpler format without job name: "is already assigned to job (\d+) on (.+)"
    const simpleMatch = part.match(/is already assigned to job (\d+) on (.+)/);
    return simpleMatch;
  };

  // Parse each part to extract job information
  for (const part of parts) {
    try {
      const jobMatch = getJobMatch(part);

      if (jobMatch) {
        // Check if this is the enhanced format (has job name)
        if (jobMatch.length === 4) {
          const [, jobIdStr, jobName, timeRange] = jobMatch;

          // Enhanced validation for job ID
          const jobId = parseInt(jobIdStr || '', 10);
          if (isNaN(jobId) || jobId <= 0) {
            console.warn('Invalid job ID found:', jobIdStr, 'in part:', part);
            continue;
          }

          // Enhanced validation for job name and time range
          const trimmedJobName = (jobName || '').trim();
          const trimmedTimeRange = (timeRange || '').trim();
          // Remove trailing sentence punctuation (e.g., ".")
          const sanitizedTimeRange = trimmedTimeRange.replace(/[.。]+$/u, '');
          // If parentheses were present but empty, keep jobName as empty string
          // Otherwise, fall back to "Job {id}" for simple format
          const finalJobName = jobName !== undefined ? trimmedJobName : `Job ${jobId}`;

          // Skip assignments with missing or malformed data
          if (!sanitizedTimeRange) {
            console.warn('Missing time range in job assignment:', part);
            continue;
          }

          // Additional validation: ensure time range has expected format
          // This can be customized based on your expected time range format
          if (!TIME_RANGE_PATTERN.test(sanitizedTimeRange)) {
            console.warn('Malformed time range format:', sanitizedTimeRange, 'in part:', part);
            continue;
          }

          assignments.push({
            jobId,
            jobName: finalJobName,
            timeRange: sanitizedTimeRange
          });
        } else {
          // Simple format (no job name)
          const [, jobIdStr, timeRange] = jobMatch;

          // Enhanced validation for job ID
          const jobId = parseInt(jobIdStr || '', 10);
          if (isNaN(jobId) || jobId <= 0) {
            console.warn('Invalid job ID found:', jobIdStr, 'in part:', part);
            continue;
          }

          // Enhanced validation for time range
          const trimmedTimeRange = (timeRange || '').trim();
          const sanitizedTimeRange = trimmedTimeRange.replace(/[.。]+$/u, '');

          // Skip assignments with missing or malformed data
          if (!sanitizedTimeRange) {
            console.warn('Missing time range in job assignment:', part);
            continue;
          }

          // Additional validation: ensure time range has expected format
          if (!TIME_RANGE_PATTERN.test(sanitizedTimeRange)) {
            console.warn('Malformed time range format:', sanitizedTimeRange, 'in part:', part);
            continue;
          }

          assignments.push({
            jobId,
            jobName: `Job ${jobId}`, // Use job ID as name when no name is provided
            timeRange: sanitizedTimeRange
          });
        }
      } else {
        // Log when regex doesn't match to help with debugging
        console.warn('No valid job assignment pattern found in part:', part);
      }
    } catch (error) {
      // Enhanced error logging
      console.warn('Failed to parse job assignment part:', part, 'Error:', error);
    }
  }

  // Filter out current job if specified
  const filteredAssignments = currentJobId ? assignments.filter((assignment) => assignment.jobId !== currentJobId) : assignments;

  return {
    assignments,
    hasMultipleAssignments: assignments.length > 1,
    filteredAssignments
  };
};

/**
 * Formats a single job assignment as a string
 * @param assignment - The job assignment to format
 * @returns Formatted string representation
 */
export const formatJobAssignment = (assignment: JobAssignment): string => {
  // If jobName is just "Job {id}", format it more cleanly
  if (assignment.jobName === `Job ${assignment.jobId}`) {
    return `Job ${assignment.jobId} - ${assignment.timeRange}`;
  }

  return `Job ${assignment.jobId} (${assignment.jobName}) - ${assignment.timeRange}`;
};

/**
 * Formats multiple job assignments as an array of formatted strings
 * @param assignments - Array of job assignments to format
 * @returns Array of formatted strings
 */
export const formatJobAssignments = (assignments: JobAssignment[]): string[] => {
  return assignments.map(formatJobAssignment);
};
